import { DetailedProduct } from 'models/DetailedProduct';
import {
  ClearEcommerceEvent,
  CopyProductTitleEvent,
  QuotationRequestEvent,
  GroupAddonEvent,
  ViewItemEvent,
  ZipCodeEvent,
  BaseGtmEvent
} from 'store/googleTagManager/types';
import { getClearEcommerceEvent } from 'store/googleTagManager/events/genericEvents';
import { createGtmQuotationProduct, mapProductToGtm } from './utils';
import {
  GtmQuotationRequestAction,
  GtmViewGroupAddonAction,
  InstallationZipCodeAction
} from 'store/googleTagManager/actions';
import { AddonGroup } from 'models/AddonGroup';
import { hasElements } from 'utils';

/**
 * Event called with a product page/product detail page is viewed.
 * Returns an array with a ClearEcommerceEvent and ViewItemEvent.
 *
 * Events containing an Ecommerce object should have a ClearEcommerceEvent before it.
 */
export const getViewItemEvent = (
  detailedProduct: DetailedProduct,
  currencyCode: string,
  addons?: Record<string, AddonGroup>
): [ClearEcommerceEvent, ViewItemEvent] => {
  const addonsAvailable = !detailedProduct.isFamily && addons && hasElements(Object.keys(addons));
  return [
    getClearEcommerceEvent(),
    {
      event: 'view_item',
      ecommerce: {
        currency: currencyCode,
        items: [mapProductToGtm({ ...detailedProduct, addonsAvailable })]
      }
    }
  ];
};

export const getCopyProductTitleEvent = (
  productTitle: string,
  productId: string
): CopyProductTitleEvent => {
  return {
    event: 'copy_text',
    copied_item_name: productTitle,
    copied_item_id: productId
  };
};

export function getInstallationZipCodeEvent({
  installationAvailable
}: InstallationZipCodeAction): ZipCodeEvent {
  return {
    event: 'installation_zip_code',
    zip_code_status: installationAvailable ? 'installation_available' : 'installation_not_available'
  };
}

export const getQuotationRequestEvent = (
  action: GtmQuotationRequestAction
): QuotationRequestEvent => {
  return {
    event: 'quotation_request',
    quotation: {
      item: createGtmQuotationProduct({
        url: action.url,
        brandName: action.brandName,
        title: action.productName
      })
    }
  };
};

export const getViewGroupAddonEvent = ({
  productId,
  groupAddons
}: GtmViewGroupAddonAction): GroupAddonEvent => {
  return {
    event: 'view_group_addon',
    viewed_product: productId,
    group_addons: groupAddons
  };
};

export const getViewPriceMatchGuaranteeEvent = (): BaseGtmEvent => {
  return {
    event: 'view_price_match_guarantee'
  };
};
